// 라우트 맵.
// / → Explorer (U32: v1 UI 단일화 — Explorer가 루트 기본 화면)
// /credentials* → Explorer redirect (v1 URL 호환)
//
// Phase 2 — DndProvider로 인증 영역 전체를 감싸 Explorer의 DnD 기능 활성화.
// DndProvider는 Toast와 TanStack Query 안에 위치해야 한다 (toast.error + mutations 사용).
//
// U36 — DensityBootstrap: 앱 마운트 시 localStorage → data-density 동기화. 한 번만 실행.
// U41 — GlobalShortcutsMount: RequireAuth 영역 안에서 useGlobalShortcuts 마운트.
//        useNavigate는 Router 컨텍스트가 있어야 하므로 RequireAuth 자식으로 위치시킴.
// U42 — Detail Panel: 2026-10-02부터 Layout 우측 고정 컬럼으로 이동 (components/Layout.tsx).
// U48 — CommandPaletteMount: Cmd+K 명령 팔레트 전역 마운트.
//        RequireAuth 영역 안에서 마운트해야 useNavigate를 사용할 수 있다.
// U53 — UndoStackMount: Undo 스택 전역 마운트.
//        UndoContext Provider + Cmd+Z 이벤트 구독 + UndoToast 렌더.
//        RequireAuth 영역 안에서 마운트. UndoContext는 ToastProvider 안에서도 동작.
// U55 — KeymapModal: ? 키로 키보드 단축키 안내 모달.
//        locker:open-shortcuts-modal 이벤트를 내부에서 구독하므로 마운트만 하면 된다.
//        RequireAuth 영역 안에서 마운트.
// U70 — InstallPrompt: PWA 홈화면 추가 안내 배너.
//        beforeinstallprompt 이벤트 기반 + iOS Safari fallback.
//        standalone 모드이면 노출 안 함. RequireAuth 영역 안에서 마운트.

import { useEffect } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import Lock from './pages/Lock';
import Explorer from './pages/Explorer';
import TagsAdmin from './pages/TagsAdmin';
import SettingsSecurity from './pages/SettingsSecurity';
import SettingsAccount from './pages/SettingsAccount';
import AccessLogs from './pages/AccessLogs';
import { RequireAuth } from './components/RequireAuth';
import { Layout } from './components/Layout';
import { DndProvider } from './components/DndProvider';
import { ToastProvider } from './ui/ToastProvider';
import { useDensity } from './hooks/useDensity';
import { useGlobalShortcuts, SHORTCUT_EVENT_UNDO } from './hooks/useGlobalShortcuts';
import { CommandPaletteMount } from './components/CommandPalette/CommandPalette';
import { useUndoStack } from './hooks/useUndoStack';
import { UndoContext } from './hooks/useUndoContext';
import { UndoToast } from './components/UndoToast';
import { KeymapModal } from './components/KeymapModal';
import { InstallPrompt } from './components/InstallPrompt';

// 앱 최상단에서 밀도 모드를 한 번 초기화하는 컴포넌트.
// useDensity의 useState initializer가 data-density를 DOM에 적용한다.
// 이 컴포넌트가 Settings에서 useDensity를 재사용해도 단일 localStorage 키로 동기화된다.
function DensityBootstrap() {
  const { density } = useDensity();
  // density가 변경될 때마다 data-density 동기화는 useDensity 내부에서 처리됨
  // useEffect는 외부 시스템(CSS 변수 반영) 확인용으로만 사용
  useEffect(() => {
    // data-density가 올바르게 설정됐는지 보장
    const current = document.documentElement.getAttribute('data-density');
    if (current !== density) {
      document.documentElement.setAttribute('data-density', density);
    }
  }, [density]);
  return null;
}

// 글로벌 단축키를 RequireAuth 컨텍스트 안에서 마운트.
// useNavigate()는 Router + 인증 컨텍스트 내부에서만 사용 가능.
function GlobalShortcutsMount() {
  useGlobalShortcuts();
  return null;
}

// Undo 스택 전역 Provider + 이벤트 구독 컴포넌트 (U53).
// RequireAuth 영역 안에서 children을 UndoContext.Provider로 감싸고,
// Cmd+Z (locker:undo 이벤트) 구독 → undoLast() 호출.
// UndoToast는 화면 우하단에 별도 렌더 (children 외부).
function UndoStackProvider({ children }: { children: React.ReactNode }) {
  const stack = useUndoStack();

  // locker:undo CustomEvent 구독 (useGlobalShortcuts가 발행)
  useEffect(() => {
    const handler = () => {
      void stack.undoLast();
    };
    window.addEventListener(SHORTCUT_EVENT_UNDO, handler);
    return () => window.removeEventListener(SHORTCUT_EVENT_UNDO, handler);
  }, [stack]);

  return (
    <UndoContext.Provider value={stack}>
      {children}
      {/* UndoToast: current 있을 때만 노출 (화면 우하단) */}
      <UndoToast stack={stack} />
    </UndoContext.Provider>
  );
}

// PWA 설치 배너 — 상세(?item)·등록(?new) 패널이 열려 있으면 숨김.
// 모바일에서 배너가 패널 하단의 저장 버튼을 가리던 문제 (2026-10-03).
function InstallPromptMount() {
  const [searchParams] = useSearchParams();
  return <InstallPrompt suppressed={searchParams.has('item') || searchParams.has('new')} />;
}

export default function App() {
  return (
    // ToastProvider: useToast 훅을 전체 앱에서 사용할 수 있도록 최상단에 마운트
    <ToastProvider>
    {/* DensityBootstrap: 앱 부팅 시 localStorage → data-density 동기화 */}
    <DensityBootstrap />
    <Routes>
      <Route path="/lock" element={<Lock />} />
      <Route
        element={
          <RequireAuth>
            {/* UndoStackProvider: UndoContext + Cmd+Z 이벤트 구독 + UndoToast 전역 마운트 (U53) */}
            <UndoStackProvider>
              {/* GlobalShortcutsMount: 인증 영역 전체 단축키 활성화 (U41) */}
              <GlobalShortcutsMount />
              {/* CommandPaletteMount: Cmd+K 명령 팔레트 전역 마운트 (U48) */}
              <CommandPaletteMount />
              {/* KeymapModal: ? 키 단축키 안내 모달 전역 마운트 (U55) */}
              <KeymapModal />
              {/* InstallPrompt: PWA 홈화면 추가 안내 배너 (U70) */}
              <InstallPromptMount />
              {/* DndProvider: Explorer DnD 기능을 위해 인증 영역 전체에 적용 */}
              <DndProvider>
                <Layout />
              </DndProvider>
            </UndoStackProvider>
          </RequireAuth>
        }
      >
        {/* 루트: Explorer가 기본 화면 (U32 — v1 단일화) */}
        <Route path="/" element={<Explorer />} />
        {/* v1 호환 redirect — /credentials* → Explorer */}
        <Route path="/credentials" element={<Navigate to="/" replace />} />
        <Route path="/credentials/:groupId" element={<Navigate to="/" replace />} />
        {/* /explorer → 루트로 redirect (중복 제거) */}
        <Route path="/explorer" element={<Navigate to="/" replace />} />
        {/* Phase 2 — 태그 관리 페이지 */}
        <Route path="/tags" element={<TagsAdmin />} />
        <Route path="/settings/security" element={<SettingsSecurity />} />
        <Route path="/settings/account" element={<SettingsAccount />} />
        <Route path="/settings/access-logs" element={<AccessLogs />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ToastProvider>
  );
}
